import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { ProspectHit } from "../../api/client.ts";
import { collectionPath, useTenant } from "../../contexts/TenantContext.tsx";
import ResearchVerdictChip from "../signals/ResearchVerdictChip.tsx";
import FormattedDate from "../FormattedDate.tsx";
import AgentRunStatusIcon from "../agent/AgentRunStatusIcon.tsx";
import { useCanSeeTranscripts } from "../agent/useCanSeeTranscripts.ts";
import { recordRoute } from "../signals/SignalRecordLink.tsx";
import { hitKindIcon, hitKindLabel } from "./hitKinds.tsx";
import { humanize } from "../../utils/format.ts";

/** A link in a hit's row: in the app (`to`) or elsewhere (`href`), or a toggle. */
export interface HitLink {
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
}

/** What a hit says about itself: its first line, the line under it, its chip. */
export function describeHit(hit: ProspectHit): { headline: ReactNode; caption: ReactNode; chip?: ReactNode } {
  const list = hit.list?.name;
  const when = <FormattedDate value={hit.occurred_at} format="datetime" />;
  if (hit.kind === "signal") {
    const s = hit.signal;
    return {
      headline: hit.headline ?? s?.record_title ?? (s ? "A signal" : "A signal since removed by a re-run"),
      caption: (
        <>
          {s?.workflow_name ?? "Signal workflow"}
          {s?.data_type && ` · ${humanize(s.data_type)}`}
          {list && ` · ${list}`} · {when}
        </>
      ),
      chip: s?.verdict ? <ResearchVerdictChip verdict={s.verdict} confidence={s.confidence} /> : undefined,
    };
  }
  if (hit.kind === "agent") {
    const name = hit.agent_run?.agent_name ?? "An agent";
    return {
      headline: `${name} added it${list ? ` to ${list}` : ""}`,
      caption: (
        <>
          {hit.headline && (
            <>
              {hit.headline}
              {" · "}
            </>
          )}
          {hit.order?.title && `${hit.order.title} · `}
          {when}
        </>
      ),
      chip: hit.agent_run ? (
        <AgentRunStatusIcon status={hit.agent_run.status} agentName={hit.agent_run.agent_name} />
      ) : undefined,
    };
  }
  if (hit.kind === "workflow") {
    return {
      headline: `Copied${list ? ` to ${list}` : ""} by a workflow`,
      caption: when,
    };
  }
  const who = hit.actor?.display_name ?? (hit.kind === "mcp" ? "An MCP client" : hit.kind === "bulk_filter" ? "A bulk add" : "Someone");
  return {
    headline: `${who} added it${list ? ` to ${list}` : ""}`,
    caption: (
      <>
        {hitKindLabel(hit.kind)}
        {hit.headline && ` · ${hit.headline}`} · {when}
      </>
    ),
  };
}

/**
 * The hits on a prospect, newest first, each with what did it, when, and
 * where it leads. `renderExpanded` draws a hit's details under it (a signal's
 * brief, a run's transcript for developers) when it is expanded.
 */
export default function ProspectHitTimeline({
  hits,
  prospectId,
  expandedHitId,
  onToggle,
  renderExpanded,
  compact = false,
}: {
  hits: ProspectHit[];
  prospectId: string;
  expandedHitId?: string | null;
  onToggle?: (hitId: string) => void;
  renderExpanded?: (hit: ProspectHit) => ReactNode;
  // Fewer links, no expansion (a card on the company page).
  compact?: boolean;
}) {
  const { tenantPath } = useTenant();
  const canSeeTranscripts = useCanSeeTranscripts();
  const linksOf = (hit: ProspectHit): HitLink[] => {
    const links: HitLink[] = [];
    if (compact) return links;
    const expandable =
      (hit.kind === "signal" && !!hit.signal) || (hit.kind === "agent" && !!hit.agent_run && canSeeTranscripts);
    if (expandable && onToggle) {
      links.push({
        label: hit.kind === "signal" ? "Research" : "Transcript",
        onClick: () => onToggle(hit.id),
        active: expandedHitId === hit.id,
      });
    }
    if (hit.signal) {
      const route = recordRoute(hit.signal.data_type, hit.signal.record_id);
      if (route) links.push({ label: "Open record", to: route });
      else if (hit.signal.record_url) links.push({ label: "Open record", href: hit.signal.record_url });
      links.push({ label: "Signal log", to: tenantPath(`/workflows/signals?signal=${hit.signal.id}`) });
    }
    if (hit.order) links.push({ label: "Order", to: `/organization/orders/${hit.order.id}` });
    if (hit.list) links.push({ label: "Open collection", to: tenantPath(collectionPath(hit.list.id)) });
    return links;
  };
  return (
    <Stack spacing={0}>
      {hits.map((hit, i) => {
        const { headline, caption, chip } = describeHit(hit);
        const links = linksOf(hit);
        const expanded = expandedHitId === hit.id;
        return (
          <Box key={hit.id} sx={{ display: "flex", gap: 1.5, position: "relative" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 28, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "action.hover",
                  color: "text.secondary",
                  flexShrink: 0,
                }}
              >
                {hitKindIcon(hit.kind, { fontSize: "small" })}
              </Box>
              {i < hits.length - 1 && (
                <Box sx={{ flexGrow: 1, width: "1px", bgcolor: "divider", my: 0.5 }} />
              )}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0, pb: i < hits.length - 1 ? 2 : 0 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1, minWidth: 0 }}>
                  {headline}
                </Typography>
                {chip}
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                {caption}
              </Typography>
              {links.length > 0 && (
                <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", mt: 0.5 }}>
                  {links.map((link) =>
                    link.onClick ? (
                      <Link
                        key={link.label}
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={link.onClick}
                        sx={{ fontWeight: link.active ? 600 : undefined }}
                      >
                        {link.active ? `Hide ${link.label.toLowerCase()}` : link.label}
                      </Link>
                    ) : link.to ? (
                      <Link key={link.label} component={RouterLink} to={link.to} variant="body2">
                        {link.label}
                      </Link>
                    ) : (
                      <Link key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" variant="body2">
                        {link.label}
                      </Link>
                    ),
                  )}
                </Stack>
              )}
              {renderExpanded && (
                <Collapse in={expanded} unmountOnExit>
                  <Box sx={{ mt: 1.5 }} data-prospect-id={prospectId}>
                    {renderExpanded(hit)}
                  </Box>
                </Collapse>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
